import { router } from '@inertiajs/react';
import { useState } from 'react';

import Card from '../../../components/Card/Card';
import CloseButton from '../../../components/CloseButton/CloseButton';
import DialogModal from '../../../components/DialogModal/DialogModal';
import HeightToggle from '../../../components/HeightToggle/HeightToggle';
import TopRight from '../../../components/TopRight/TopRight';
import ForgotPasswordForm from '../ForgotPasswordForm/ForgotPasswordForm';
import LoginForm from '../LoginForm/LoginForm';
import WaitingForLoginModal from '../WaitingForLoginModal/WaitingForLoginModal';

import DisplayOptions from './types/DisplayOptions';

type LoginAndForgotPasswordCardProps = {
    loginUrl: string;
    requestPasswordResetUrl?: string;
    onClose?: () => void;
    onReady: () => void;
    display: DisplayOptions;
};

const LoginAndForgotPasswordCard = ({
    loginUrl,
    requestPasswordResetUrl,
    onClose,
    onReady,
    display,
}: LoginAndForgotPasswordCardProps) => {
    const [displayForgotForm, setDisplayForgotForm] = useState(false);
    const [waitingForLogin, setWaitingForLogin] = useState(false);
    const toggleForgotPasswordForm = () =>
        setDisplayForgotForm(!displayForgotForm);

    const closeAndReload = () => {
        setDisplayForgotForm(false);
        setWaitingForLogin(false);
        router.reload();
        onReady();
    };

    const displayedInOverlay = display === 'force';
    const noop = () => {};

    if (!loginUrl) {
        return null;
    }

    if (waitingForLogin) {
        return <WaitingForLoginModal onClose={closeAndReload} />;
    }

    const card = (
        <Card>
            {!displayedInOverlay && (
                <TopRight top="negativeSmall" right="negativeSmall">
                    <CloseButton onClick={onClose} variant="clear" />
                </TopRight>
            )}

            {displayForgotForm && requestPasswordResetUrl ? (
                <ForgotPasswordForm
                    layout={displayedInOverlay ? 'stacked' : 'inline'}
                    actionUrl={requestPasswordResetUrl}
                    onComplete={() => {
                        setWaitingForLogin(true);
                        setDisplayForgotForm(false);
                    }}
                    onCancel={toggleForgotPasswordForm}
                />
            ) : (
                <LoginForm
                    layout={displayedInOverlay ? 'stacked' : 'inline'}
                    onForgotPassword={
                        requestPasswordResetUrl
                            ? toggleForgotPasswordForm
                            : undefined
                    }
                    actionUrl={loginUrl}
                />
            )}
        </Card>
    );

    if (displayedInOverlay) {
        return (
            <DialogModal
                onCloseRequested={onClose ?? noop}
                disableCloseWithEscape
                hideCloseButton={!onClose}
                isOpen
                unstyled
                width="regular"
            >
                {card}
            </DialogModal>
        );
    }

    return <HeightToggle show={display !== 'hidden'}>{card}</HeightToggle>;
};

export default LoginAndForgotPasswordCard;
